import React, { useState, useEffect } from 'react';
import { X, Share2, Copy, Check, Loader, Trash2, FolderOpen } from 'lucide-react';
import { toast } from 'react-toastify';
import folderService from '../../services/folderServices';

/**
 * ShareFolderModal
 * Generates (or reveals) a unique share code for a folder owned by the user.
 * Anyone with the code can import a copy of the folder + every element inside
 * it through "Get Shared Element". Mirrors ShareElementModal in shape and
 * Stop Sharing behaviour so the folder and element flows feel identical.
 *
 * Props:
 *   isOpen   — boolean
 *   folder   — the folder object being shared (needs .id or ._id, .name, optional .color)
 *   onClose  — callback()
 */
export default function ShareFolderModal({ isOpen, folder, onClose }) {
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    if (!isOpen || !folder) return;
    setStatus('loading');
    setCopied(false);
    folderService
      .generateFolderShareCode(folder.id || folder._id)
      .then((res) => {
        if (res?.shareCode) {
          setCode(res.shareCode);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      })
      .catch((err) => {
        setStatus('error');
        toast.error(err?.message || 'Failed to generate share code');
      });
  }, [isOpen, folder]);

  if (!isOpen || !folder) return null;

  const handleCopyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Share code copied to clipboard');
    } catch (_) {
      toast.error('Unable to copy — please copy it manually');
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm('Stop sharing this folder? The code will no longer work and recipients will not be able to copy it.')) return;
    setRevoking(true);
    try {
      await folderService.unshareFolder(folder.id || folder._id);
      toast.success('Sharing disabled');
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to stop sharing');
    } finally {
      setRevoking(false);
    }
  };

  const folderColor = folder.color || '#6a1fbf';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md bg-[#0d0d1a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-purple-400" />
            <h2 className="text-base font-semibold text-white">Share Folder</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="px-6 pt-4 pb-2">
          <p className="text-xs text-gray-400 mb-1">Sharing folder:</p>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg">
            <FolderOpen className="w-3.5 h-3.5" style={{ color: folderColor }} />
            <span className="text-sm text-white font-medium truncate max-w-[300px]">
              {folder.name}
            </span>
          </div>
        </div>

        <div className="px-6 pb-4 min-h-[160px]">
          {status === 'loading' && (
            <div className="flex items-center justify-center py-10">
              <Loader className="w-6 h-6 text-purple-400 animate-spin" />
            </div>
          )}

          {status === 'error' && (
            <div className="py-8 text-center">
              <p className="text-sm text-red-300">
                Could not generate a share code. Please close and try again.
              </p>
            </div>
          )}

          {status === 'ready' && (
            <>
              <p className="text-sm text-gray-300 mb-3">
                Give this code to anyone who should be able to import a copy of
                this folder and every element inside it. They can use{' '}
                <span className="text-purple-300">Add Shared Elements</span> in
                their own Elements section.
              </p>

              <div className="flex items-stretch gap-2">
                <div className="flex-1 px-4 py-3 bg-white/5 border border-purple-500/30 rounded-lg font-mono text-xl tracking-[0.25em] text-white text-center select-all">
                  {code}
                </div>
                <button
                  onClick={handleCopyCode}
                  className="flex items-center justify-center gap-2 px-4 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
                  title="Copy code"
                >
                  {copied ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-3">
                This code stays with the original folder. Copies made by others
                are independent — they don't carry the code forward, and changes
                you make later won't sync to them.
              </p>
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
          {status === 'ready' ? (
            <button
              onClick={handleRevoke}
              disabled={revoking}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-300 hover:text-red-200 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
            >
              {revoking ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Stop Sharing
            </button>
          ) : (
            <span />
          )}

          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
